import { useState } from 'react'
import type { ReactElement } from 'react'
import { AppRoot } from '../components/AppRoot'
import { signOut } from '../lib/auth'
import { Button } from '../ui'

/**
 * Shown at `/` when `decideGate` returns `'DISABLED'` — an admin has disabled the account.
 * Offers only sign-out: there's nothing to retry and no self-service path, per CLAUDE.md's rule
 * that a warning must state its actual reason rather than leave someone guessing.
 *
 * Wrapped in exactly one `<AppRoot>`, like every other top-level screen (`JoinScreen`'s
 * not-signed-in branch explains why exactly one, never zero, never two). Without it nothing
 * renders inside `.sl-root`, `base.css` never sets `--ink`, and under a dark OS preference this
 * screen is black text on the near-black `--paper` that `app.css` paints onto `body`.
 */
export function DisabledNotice(): ReactElement {
  const [signingOut, setSigningOut] = useState(false)
  const [signOutError, setSignOutError] = useState<string | null>(null)

  async function handleSignOut() {
    setSigningOut(true)
    setSignOutError(null)
    const { error } = await signOut()
    // No navigation needed on success — SessionProvider's onAuthStateChange subscription picks
    // up the cleared session and AppRoutes redirects to /sign-in on its own next render.
    if (error) {
      setSignOutError(error.message)
      setSigningOut(false)
    }
  }

  return (
    <AppRoot>
      <div className="sl-stack">
        <div>
          <h1 className="sl-heading">Your account has been disabled.</h1>
          <p className="sl-hint">Contact an admin if you think this is a mistake.</p>
        </div>
        <Button variant="secondary" block disabled={signingOut} onClick={() => void handleSignOut()}>
          Sign out
        </Button>
        {signOutError ? (
          <p className="sl-hint" role="alert">
            {signOutError}
          </p>
        ) : null}
      </div>
    </AppRoot>
  )
}
